import { useState } from 'react';
import { MailIcon, AlertCircleIcon } from './icons.js';

/**
 * Inline feedback channel. Each surface (Knowledge Agent answer card,
 * Documents row) embeds this component so a user spotting a wrong author
 * / outdated detail / miscited answer can flag it without leaving the
 * page.
 *
 * Mechanism: opens the user's default mail client with To, Subject, and
 * Body pre-filled. The user edits and sends from their own inbox; the
 * destination address is read at build time from VITE_FEEDBACK_EMAIL.
 * If unset, the button is hidden (no hardcoded fallback — set the env
 * var per-deployment).
 *
 * Why mailto, not a backend POST: at small user counts, server-side SMTP
 * is operational tax (SPF/DKIM, bounces, deliverability) we don't need
 * yet. Email thread = audit trail.
 *
 * Variants:
 *   - `variant="link"` — small inline link in a row/card. Used by
 *     Documents.
 *   - `variant="modal"` — opens a confirmation modal first. Used by
 *     the Knowledge Agent thumbs-down promotion (gives the user a "No,
 *     just 👎" escape so the existing thumbs-down behaviour isn't
 *     hijacked).
 */

const TO = import.meta.env.VITE_FEEDBACK_EMAIL || '';

export type ChangeRequestContext =
  | {
      surface: 'kennisagent';
      chatId: string | null;
      query: string;
      answerExcerpt: string;
      citations: { chunkId: string; filename?: string; documentTitle?: string }[];
    }
  | {
      surface: 'documenten';
      filename: string;
      documentTitle: string;
      author: string;
      specialisations: string[];
    };

function surfaceLabel(s: ChangeRequestContext['surface']): string {
  if (s === 'kennisagent') return 'Knowledge Agent';
  return 'Document';
}

function shortRef(ctx: ChangeRequestContext): string {
  if (ctx.surface === 'kennisagent') {
    const t = ctx.query.trim();
    return t.length > 60 ? t.slice(0, 60) + '…' : t;
  }
  return ctx.documentTitle || ctx.filename;
}

function contextLines(ctx: ChangeRequestContext): string[] {
  if (ctx.surface === 'kennisagent') {
    const lines = [
      `Question: ${ctx.query}`,
      `Answer (first 200 chars): ${ctx.answerExcerpt.slice(0, 200)}${ctx.answerExcerpt.length > 200 ? '…' : ''}`,
    ];
    if (ctx.chatId) lines.push(`Chat ID: ${ctx.chatId}`);
    if (ctx.citations.length > 0) {
      lines.push('Cited sources:');
      for (const c of ctx.citations) {
        const label = c.documentTitle || c.filename || c.chunkId;
        lines.push(`  - ${label} (chunk ${c.chunkId.slice(0, 12)}…)`);
      }
    }
    return lines;
  }
  return [
    `File: ${ctx.filename}`,
    `Title: ${ctx.documentTitle}`,
    `Author: ${ctx.author || '(empty)'}`,
    `Specialisations: ${ctx.specialisations.length ? ctx.specialisations.join(', ') : '(none)'}`,
  ];
}

function buildMailto(ctx: ChangeRequestContext, userNote: string): string {
  const subject = `[Knowledgebase] ${surfaceLabel(ctx.surface)}: ${shortRef(ctx)}`;
  const body = [
    userNote.trim() || '(describe what is incorrect or missing)',
    '',
    '— Context (please leave this so we can fix it) —',
    `URL: ${typeof window !== 'undefined' ? window.location.href : ''}`,
    `Time: ${new Date().toISOString()}`,
    `Surface: ${ctx.surface}`,
    ...contextLines(ctx),
  ].join('\n');
  return `mailto:${TO}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/**
 * Standalone link variant for Documents. Click → modal with textarea →
 * "Open mail" opens the user's mail client.
 */
export function ChangeRequestLink({
  context,
  className = '',
}: {
  context: ChangeRequestContext;
  className?: string;
}): JSX.Element | null {
  const [open, setOpen] = useState(false);
  if (!TO) return null;
  const label = 'Send feedback';
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className={`inline-flex items-center gap-1 text-[11px] text-ink-900/55 hover:text-brand-700 hover:underline ${className}`}
      >
        <MailIcon size={11} aria-hidden="true" />
        {label}
      </button>
      {open && <ChangeRequestModal context={context} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * Modal — used directly by the Knowledge Agent's thumbs-down promotion
 * (where we want a "No, just 👎" escape) and via ChangeRequestLink
 * for the other surface.
 *
 * onJustVote is the "I only wanted to vote" escape — invoked by the
 * Knowledge Agent integration to record the down-vote and dismiss
 * without a mailto. Not shown when the modal is opened via the link
 * variant (no vote to preserve there).
 */
export function ChangeRequestModal({
  context,
  onClose,
  onJustVote,
}: {
  context: ChangeRequestContext;
  onClose: () => void;
  onJustVote?: () => void;
}): JSX.Element | null {
  const [note, setNote] = useState('');
  if (!TO) return null;
  function open(): void {
    window.location.href = buildMailto(context, note);
    onClose();
  }
  return (
    <div
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-ink-900/40 p-6"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="mt-16 w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="wv-title"
      >
        <h2 id="wv-title" className="font-display text-lg font-bold text-ink-900">
          Send feedback
        </h2>
        <p className="mt-1 text-sm text-ink-900/65">
          Briefly describe what is incorrect or what should be added. We send it to the
          Knowledgebase administrator.
        </p>

        <textarea
          autoFocus
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={4}
          placeholder="Briefly describe what is incorrect or what should be added…"
          className="mt-4 w-full rounded-md border border-brand-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200/40"
        />

        <p className="mt-2 text-[11px] text-ink-900/45">
          Your mail client will open with the context already filled in. You can edit the message
          before sending.
        </p>

        <div className="mt-3 flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
          <AlertCircleIcon size={13} className="mt-px flex-shrink-0" aria-hidden="true" />
          <span>
            For urgent issues or formal complaints, please use your team's regular escalation
            channel.
          </span>
        </div>

        <footer className="mt-5 flex items-center justify-end gap-2">
          {onJustVote && (
            <button
              type="button"
              onClick={() => {
                onJustVote();
                onClose();
              }}
              className="rounded-lg px-3 py-1.5 text-sm text-ink-900/60 hover:bg-brand-50 hover:text-ink-900"
            >
              No, just 👎
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm text-ink-900/60 hover:bg-brand-50 hover:text-ink-900"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={open}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
          >
            <MailIcon size={13} />
            Open mail
          </button>
        </footer>
      </div>
    </div>
  );
}
